



























































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import { mdbRow, mdbCol, mdbInput, mdbSelect } from '@folky-tools/mdbvue';
import { Business } from '@/models/businesses/business';
import { Project } from '@/models/projects/project';
import { Pricing } from '@/models/pricings/pricing';
import { Association } from '@/models/businesses/association';
import { BusinessAssociation } from '@/models/businesses/business-association';
import { customAbility } from '@/models/users/user-permissions/custom-ability';
import { UserEntityPermissionsType, UserPermissionAction } from '@/models/users/user-permissions/user-entity-permissions';
import { BusinessActivity } from '@/models/businesses/business-activity';
import { businessesService, projectsService, pricingsService, authorizationManagerService } from '@/services';
import { required } from 'vuelidate/lib/validators';
import { BusinessType } from '@/models/businesses/business-type';
import { LinkedBusiness } from '@/models/businesses/linked-business';

@Component({
    components: {
        mdbRow,
        mdbCol,
        mdbInput,
        mdbSelect
    },
    validations: {
        business: {
            tradeName: { required },
            projects: { required },
            pricings: { required }
        }
    }
})
export default class BusinessBasicData extends Vue {
    @Prop(Business) private business!: Business;
    @Prop() private newBusinessView?: boolean;
    @Prop() private singleColumnForm?: boolean;
    @Prop() private disableProjectsSelection?: boolean;
    @Prop() private disablePricingsSelection?: boolean;
    private projects: Project[] = [];
    private pricings: Pricing[] = [];
    private associations: Association[] = [];
    private topLevelBusinesses: Business[] = [];
    private mainActivities: BusinessActivity[] = [];
    private secondaryActivities: BusinessActivity[] = [];

    private async mounted(): Promise<void> {
        const projects: Project[] = await projectsService.getProjects();
        if (this.business.projects.length) {
            const projectIds = this.business.projects.map(p => p.id);
            projects.forEach(p => {
                if (projectIds.includes(p.id)) {
                    p.selected = true;
                    p.disabled = true;
                }
            });
        }
        this.projects = projects;

        const mainActivities = await businessesService.getActivities();
        const secondaryActivities = mainActivities.map(a => a.clone());
        if (this.business.mainActivity && this.business.mainActivity.id > 0) {
            mainActivities.find(a => a.id === this.business.mainActivity!.id)!.selected = true;
        }
        if (this.business.secondaryActivity && this.business.secondaryActivity.id > 0) {
            secondaryActivities.find(a => a.id === this.business.secondaryActivity!.id)!.selected = true;
        }

        const mainActivitiesHash: any = {};
        const secondaryActivitiesHash: any = {};
        for (const activity of mainActivities) {
            if (mainActivitiesHash[activity.parentId!] === undefined) {
                mainActivitiesHash[activity.parentId!] = [];
            }
            mainActivitiesHash[activity.parentId!].push(activity);
        }
        for (const activity of secondaryActivities) {
            if (secondaryActivitiesHash[activity.parentId!] === undefined) {
                secondaryActivitiesHash[activity.parentId!] = [];
            }
            secondaryActivitiesHash[activity.parentId!].push(activity);
        }

        this.mainActivities = this.hierarchySortActivities(mainActivities, mainActivitiesHash, null, []);
        this.secondaryActivities = this.hierarchySortActivities(secondaryActivities, secondaryActivitiesHash, null, []);

        this.addDefaultActivities();
    }

    private hierarchySortActivities(allActivities: BusinessActivity[], activitiesHash: any, key: number | null, result: BusinessActivity[]): BusinessActivity[] {
        if (activitiesHash[key!] === undefined) {
            return [];
        }

        const activities: BusinessActivity[] = activitiesHash[key!].sort((left: BusinessActivity, right: BusinessActivity) => {
            return left.text!.localeCompare(right.text!, this.$i18n.locale, { sensitivity: 'base' });
        });

        for (const activity of activities) {
            if (activity.parentId) {
                const parentActivity = allActivities.find(a => a.id === activity.parentId);
                activity.levelTab = activitiesHash[parentActivity!.parentId!][0].levelTab + '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
            }
            result.push(activity);
            this.hierarchySortActivities(allActivities, activitiesHash, activity.id!, result);
        }

        return result;
    }

    private async onChangeProject(value: number[], text: string): Promise<void> {
        if (value) {
            const selectedProjects: Project[] = [];
            this.projects.forEach(p => {
                if (typeof value === 'number') {
                    if (value === p.id) {
                        selectedProjects.push(p);
                    }
                } else {
                    if (value.includes(p.id)) {
                        selectedProjects.push(p);
                    }
                }
            });
            if (this.newBusinessView) {
                this.business.projects = selectedProjects;
            } else {
                selectedProjects.forEach(p => {
                    if (!this.business.projects.some(proj => proj.id === p.id)) {
                        this.business.projects.push(p);
                    }
                });
            }
            const selectedProjectIds = selectedProjects.map(p => p.id);

            const pricings: Pricing[] = ([] = await pricingsService.getPricings(selectedProjectIds));
            if (this.business.pricings.length) {
                const pricingtIds = this.business.pricings.map(p => p.id);
                pricings.forEach(p => {
                    if (pricingtIds.includes(p.id)) {
                        p.selected = true;
                    }
                });
            }

            this.pricings = pricings;

            let associations: Association[] = await businessesService.getAssociations(selectedProjectIds);
            if (this.newBusinessView) {
                if (!authorizationManagerService.isAdmin) {
                    const rule = customAbility.rules.find(r => r.subject === UserEntityPermissionsType[UserEntityPermissionsType.Associations] && (r.actions === UserPermissionAction.Owner || r.actions === UserPermissionAction.Write));
                    const associationIds: number[] = (rule!.conditions as any).permissionsIdToCheck.$in;
                    associations = associations.filter(a => associationIds.some(id => id === a.id));
                }
            }
            if (this.business.associations && this.business.associations.length > 0) {
                associations.forEach(association => {
                    if (this.business.associations.some(a => a.associationId === association.id)) {
                        association.selected = true;
                    }
                });
            }
            this.associations = associations;

            this.topLevelBusinesses =
                this.business.type === BusinessType.NeighborhoodCommunity
                    ? await businessesService.getBusinessByTypeAndProjectIds(BusinessType.Office, selectedProjectIds)
                    : this.business.type === BusinessType.Parish
                    ? await businessesService.getBusinessByTypeAndProjectIds(BusinessType.ParishUnit, selectedProjectIds)
                    : this.business.type === BusinessType.ParishUnit
                    ? await businessesService.getBusinessByTypeAndProjectIds(BusinessType.Vicarage, selectedProjectIds)
                    : this.business.type === BusinessType.Vicarage
                    ? await businessesService.getBusinessByTypeAndProjectIds(BusinessType.Diocese, selectedProjectIds)
                    : this.business.type === BusinessType.Business || this.business.type === BusinessType.Carrier
                    ? await businessesService.getBusinessByTypeAndProjectIds(BusinessType.Grouping, selectedProjectIds)
                    : [];
            if (this.business.topLevelBusinesses && this.business.topLevelBusinesses.length > 0) {
                this.topLevelBusinesses.forEach(topLevelBusiness => {
                    if (this.business.topLevelBusinesses.some(tb => tb.topLevelBusinessId === topLevelBusiness.id)) {
                        topLevelBusiness.selected = true;
                    }
                });
            }
        } else {
            this.pricings = [];
            this.business.pricings = [];

            this.associations = [];
            this.business.associations = [];

            this.topLevelBusinesses = [];
            this.business.topLevelBusinesses = [];
        }
    }

    private onChangePricing(value: number[], text: string) {
        if (value) {
            const selectedPricings: Pricing[] = [];
            this.pricings.forEach(p => {
                if (typeof value === 'number') {
                    if (value === p.id) {
                        selectedPricings.push(p);
                    }
                } else {
                    if (value.includes(p.id)) {
                        selectedPricings.push(p);
                    }
                }
            });
            this.business.pricings = selectedPricings;
        } else {
            this.business.pricings = [];
        }
    }

    private onChangeMainActivity(value: number, text: string) {
        if (value) {
            const selectedMainActivity = this.mainActivities.find(a => a.id === value);

            this.business.mainActivity = new BusinessActivity({
                id: selectedMainActivity!.id,
                name: selectedMainActivity!.name,
                localizedName: JSON.stringify(selectedMainActivity!.localizedName),
                parentId: selectedMainActivity!.parentId
            });
        } else {
            this.business.mainActivity = undefined;
        }
    }

    private onChangeSecondaryActivity(value: number, text: string) {
        if (value) {
            const selectedSecondaryActivity = this.secondaryActivities.find(a => a.id === value);
            this.business.secondaryActivity = new BusinessActivity({
                id: selectedSecondaryActivity!.id,
                name: selectedSecondaryActivity!.name,
                localizedName: JSON.stringify(selectedSecondaryActivity!.localizedName),
                parentId: selectedSecondaryActivity!.parentId
            });
        } else {
            this.business.secondaryActivity = undefined;
        }
    }

    private onChangeAssociation(value: number, text: string) {
        this.business.associations = [];

        if (value) {
            const selectedAssociation = this.associations.find(a => a.id === value);
            this.business.associations.push(
                new BusinessAssociation({
                    associationId: selectedAssociation!.id,
                    associationName: selectedAssociation!.name
                })
            );
        }
    }

    private onChangeAssociations(values: number[], texts: string[]) {
        this.business.associations = [];

        if (values && values.length > 0) {
            values.forEach(value => {
                const selectedAssociation = this.associations.find(a => a.id === value);
                this.business.associations.push(
                    new BusinessAssociation({
                        associationId: selectedAssociation!.id,
                        associationName: selectedAssociation!.name
                    })
                );
            });
        } else {
            this.associations.forEach(a => {
                a.selected = false;
            });
        }
    }

    private onChangeTopLevelBusinesses(values: number[], texts: string[]) {
        this.business.topLevelBusinesses = [];

        if (values && values.length > 0) {
            values.forEach(value => {
                const selectedTopLevelBusiness = this.topLevelBusinesses.find(a => a.id === value);
                this.business.topLevelBusinesses.push(
                    new LinkedBusiness({
                        topLevelBusinessId: selectedTopLevelBusiness!.id,
                        topLevelBusinessTradeName: selectedTopLevelBusiness!.tradeName
                    })
                );
            });
        } else {
            this.topLevelBusinesses.forEach(tb => {
                tb.selected = false;
            });
        }
    }

    private hasErrors(): boolean {
        this.$v.business.$touch();
        if (this.$v.business.$error) {
            return true;
        }
        return false;
    }

    private addDefaultActivities(): void {
        this.mainActivities.unshift(BusinessActivity.Default());
        this.secondaryActivities.unshift(BusinessActivity.Default());
    }
}
